import {Injectable, signal} from '@angular/core'
import { injectLogger } from '@/services/logger.service'
import {TranslocoService} from '@jsverse/transloco'
import {ThemeService} from './services/theme.service'
import {AuthService} from './services/auth.service'
import {SiteConfigService} from './services/site-config.service'
import {sleep} from './utils/promises'
import {AudioService} from './services/audio.service'
import { WalletService } from './services/wallet.service'
import { SportsTreeLiveService } from './features/betting-offer/live/sports-tree-live.service'


const APP_INIT_MAX_BATCH_RETRIES = 20
const APP_INIT_RETRY_TIMEOUT_MS = 500

/**
 * Manages all application initialization logic.
 */
@Injectable({ providedIn: 'root' })
export class AppInitService {
	public initError = signal<unknown | null>(null)
	public isLoading = signal<boolean>(true)
	public successStatus = signal<boolean>(false)
	private testWSConnectionError = false // Set to true to simulate WS failure and test retry logic
	private readonly logger = injectLogger('AppInitService')
	private batchRetryCount = 0
	private totalRetryCount = 0

	constructor(
		private translocoService: TranslocoService,
		private siteConfigService: SiteConfigService,
		private themeService: ThemeService, // Initializes the theme in a constructor
		private authService: AuthService,
		private audioService: AudioService,
		private walletService: WalletService,
		private liveSportsOverviewService: SportsTreeLiveService
	) {}

	public async init(): Promise<void> {
		try {
			// Must wait for app load before resolving this promise
			const success = await this.attemptToLoadApp()
			if (!success) {
				this.retryAppLoad()
			}
		} catch (err) {
			this.logger.error('Init error:', err)
		}
	}

	public async retryAppLoad(): Promise<boolean> {
		this.isLoading.set(true)
		for (this.batchRetryCount = 1; this.batchRetryCount <= APP_INIT_MAX_BATCH_RETRIES; this.batchRetryCount++) {
			this.totalRetryCount++
			this.logger.debug('Retry attempt:', this.batchRetryCount, ' Total attempts:', this.totalRetryCount)
			await this.attemptToLoadApp()

			if (this.initError() === null) {
				this.isLoading.set(false)
				this.successStatus.set(true)
				return true // Success
			}
			await sleep(APP_INIT_RETRY_TIMEOUT_MS)
		}

		this.logger.warn('Max retries reached without success.')
		this.isLoading.set(false)
		this.successStatus.set(false)
		return false // Failed after all retries
	}

	private async attemptToLoadApp(): Promise<boolean> {
		try {
			if (this.testWSConnectionError && this.totalRetryCount <= APP_INIT_MAX_BATCH_RETRIES) {
				throw new Error('WebSocket connection failed') // Simulate failure to test retry logic
			}
			// Obtain tokens, open + authenticate the socket and load the site config.
			// Socket connect and reconnects are owned by ng-session.
			await this.authService.initSession()
			// Reset on success
			this.batchRetryCount = 0
			this.totalRetryCount = 0
			this.initError.set(null)
			return true
		} catch (err) {
			this.initError.set(err)
			return false
		}
	}
}
